import { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import { STATIC_STRINGS } from "../../../../utils/Constants";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { CELL_TYPES, FILTER_KEYS } from "../../../../utils/enums";
import { formatDateTime, formatPercentage, isEmpty } from "../../../../utils";
import { Tooltip } from "@mui/material";

const RenderTable = ({
  filters,
  gridData,
  searchTerm,
  selectedRows,
  updateSelectedRows,
  visibleColumns,
}: any) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const convertFormDataToGridData = (formFields) => {
    let res = {};
    formFields.map((formField) => {
      res[`${formField.id}`] = formField.value;
    });
    return res;
  };

  const filteredData = useMemo(() => {
    if (!gridData) return [];

    const searchQuery = searchTerm.toLowerCase();
    const progressStatusFilter = filters[FILTER_KEYS.PROGRESS_STATUS];
    const userStatusFilter = filters[FILTER_KEYS.USER_STATUS];
    const trustScoreFilter = filters[FILTER_KEYS.TRUST_SCORE];
    const marksFilter = filters[FILTER_KEYS.MARKS];
    const strongAreasFilter = filters[FILTER_KEYS.STRONG_AREAS];

    return gridData
      .filter((candidate) => {
        const {
          name,
          progress,
          status,
          trustScore,
          scorePercentage,
          strongAreas,
        } = candidate;

        const candidateName = name.toLowerCase();
        const matchSearch = candidateName.includes(searchQuery);

        const matchProgress =
          !progressStatusFilter || progress === progressStatusFilter;

        const candidateStatus = status?.toUpperCase();
        const matchUserStatus =
          !userStatusFilter || candidateStatus === userStatusFilter;

        const candidateTrustScore = Number(trustScore * 100);
        const matchTrustScore =
          !trustScoreFilter ||
          (candidateTrustScore >= trustScoreFilter[0] &&
            candidateTrustScore <= trustScoreFilter[1]);

        const matchMarks =
          !marksFilter ||
          (scorePercentage === null &&
            marksFilter[0] === 0 &&
            marksFilter[1] === 100) ||
          (parseInt(scorePercentage) >= marksFilter[0] &&
            parseInt(scorePercentage) <= marksFilter[1]);

        const matchStrongAreas =
          !strongAreasFilter ||
          strongAreasFilter.length === 0 ||
          (Array.isArray(strongAreas) &&
            strongAreas.length > 0 &&
            strongAreasFilter.some((area) => strongAreas.includes(area)));

        return (
          matchSearch &&
          matchProgress &&
          matchUserStatus &&
          matchTrustScore &&
          matchMarks &&
          matchStrongAreas
        );
      })
      .map((datum) => {
        return {
          id: datum.id,
          name: generateCellData(datum.name, CELL_TYPES.text),
          email: generateCellData(datum.email, CELL_TYPES.text),
          totalMarks: generateCellData(
            datum.scorePercentage,
            CELL_TYPES.percent,
            STATIC_STRINGS.NOT_STARTED
          ),
          trustScore: generateCellData(
            formatPercentage(Number(datum.trustScore), 0),
            CELL_TYPES.percent
          ),
          addedOn: generateCellData(datum.createdAt, CELL_TYPES.dateTime),
          attemptedOn: generateCellData(
            datum.assessmentStartedTime,
            CELL_TYPES.dateTime
          ),
          areasOfImprovement: generateCellData(
            datum.weakAreas,
            CELL_TYPES.chip
          ),
          strongAreas: generateCellData(datum.strongAreas, CELL_TYPES.chip),
          ...convertFormDataToGridData(datum.formFields),
        };
      });
  }, [searchTerm, gridData, filters]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedId(null);
  };

  function generateCellData(cellData, type, emptyMsg?) {
    type == CELL_TYPES.chip && console.log(cellData);
    if (isEmpty(cellData)) {
      return emptyMsg ? (
        <span className="text-danger text-base font-medium">{emptyMsg}</span>
      ) : (
        <span>---</span>
      );
    }
    switch (type) {
      case CELL_TYPES.text:
        return <span className="text-base font-medium">{cellData}</span>;
      case CELL_TYPES.percent:
        return <span className="text-base font-medium">{cellData}%</span>;
      case CELL_TYPES.dateTime:
        return (
          <span className="text-base font-medium">
            {formatDateTime(cellData)}
          </span>
        );
      case CELL_TYPES.chip:
        return (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs font-medium chip">{cellData[0]}</span>
            {cellData.length > 1 && (
              <Tooltip title={cellData.slice(1).join(", ")}>
                <span className="text-base font-medium text-neutral-dark ">
                  +{cellData.length - 1}{" "}
                </span>
              </Tooltip>
            )}
          </div>
        );
      default:
        return <>{cellData}</>;
    }
  }

  return (
    <div className="w-full overflow-hidden mb-4">
      <TableContainer className="border rounded-lg overflow-x-auto max-w-full">
        <Table className="min-w-full">
          <TableHead className="bg-gray-200 ">
            <TableRow>
              <TableCell className="sticky left-0 z-10  bg-gray-200 min-w-[300px]">
                <Checkbox
                  onChange={(e) =>
                    updateSelectedRows(
                      e.target.checked ? gridData.map((d) => d.id) : []
                    )
                  }
                  checked={selectedRows.length === gridData.length}
                />
                <span className="text-base font-semibold">
                  {STATIC_STRINGS.TABLE_HEADERS.CANDIDATE}
                </span>
              </TableCell>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.key}
                  id={col.key}
                  className="whitespace-nowrap min-w-[150px]"
                >
                  <div className="flex justify-center w-full">
                    <span className="text-base font-semibold">{col.label}</span>
                  </div>
                </TableCell>
              ))}

              <TableCell className="sticky right-0 bg-gray-200 z-10 min-w-[200px]">
                <div className="flex justify-center w-full">
                  <span className="text-base font-semibold">
                    {STATIC_STRINGS.TABLE_HEADERS.ACTIONS}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="sticky left-0 bg-white z-10 min-w-[500px]">
                  <Checkbox
                    checked={selectedRows.includes(row.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        updateSelectedRows([...selectedRows, row.id]);
                      } else {
                        updateSelectedRows(
                          selectedRows.filter((id) => id !== row.id)
                        );
                      }
                    }}
                  />
                  {row.name}( {row.email})
                </TableCell>
                {visibleColumns.map((col) => (
                  <TableCell
                    key={`${col.key}-${row.id}`}
                    id={`${col.key}-${row.id}`}
                    className="min-w-[200px]"
                  >
                    <div className="flex justify-center w-full">
                      {row[col.key]}
                    </div>
                  </TableCell>
                ))}

                <TableCell className="sticky right-0 bg-white z-10">
                  <div className="flex items-center justify-end w-full">
                    <button className={classNames("mr-2 btn btn-outline")}>
                      {STATIC_STRINGS.VIEW_REPORT}
                    </button>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, row.id);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                    {selectedId === row.id && (
                      <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={(e: Event) => {
                          e.stopPropagation();
                          handleMenuClose();
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/edit/${selectedId}`);
                          }}
                        >
                          {STATIC_STRINGS.MENU_ITEMS.EDIT}
                        </MenuItem>
                      </Menu>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RenderTable;
